import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success(`Welcome back, ${user.full_name}!`);
      
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/dashboard/admin');
      } else if (user.role === 'project_manager') {
        navigate('/dashboard/pm');
      } else if (user.role === 'vendor') {
        navigate('/dashboard/vendor');
      } else if (user.role === 'creative_team') {
        navigate('/dashboard/creative');
      } else {
        navigate('/dashboard/client');
      }
    } catch (error) {
      toast.error('Invalid email or password');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo/Brand */}
          <Link to="/" className="flex justify-center mb-8">
            <div className="text-3xl font-bold tracking-tight">
              <span className="text-[#D4AF37]">NEHPETS</span>
              <span className="text-white">+</span>
            </div>
          </Link>

          {/* Login Form */}
          <div className="glass-effect p-8 space-y-6">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-medium mb-2">Welcome Back</h1>
              <p className="text-white/60 text-sm">Login to access your dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Email</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Password</label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] disabled:opacity-50"
                data-testid="login-submit-button"
              >
                {loading ? 'Logging in...' : (
                  <>
                    Login
                    <LogIn className="ml-2" size={16} />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center pt-4 border-t border-white/5">
              <p className="text-white/60 text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="register-link">
                  Register here
                </Link>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <Link to="/" className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;